/**
 * AtlasView — Sprint 2
 * The private atlas: a quiet record of where the traveller has been. Personal
 * figures and personal records only — never a leaderboard, never a position
 * relative to anyone else. Recomputed on open (cheap, counts-only).
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { recomputeAtlas } from '../utils/atlasManager';
import HomeButton from './HomeButton.jsx';
import PostcardCollection from './PostcardCollection.jsx';
import AtlasBook from './atlas/AtlasBook.jsx';
import theme from '../styles/theme';

export default function AtlasView({ onNavigate }) {
  const { t, i18n } = useTranslation('common');
  const [atlas, setAtlas] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const a = await recomputeAtlas().catch(() => null);
      if (!cancelled) { setAtlas(a); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

  const isEmpty = !atlas || atlas.tripsSealed === 0;

  const figures = atlas ? [
    { value: atlas.cities.length,    label: t('atlas.cities') },
    { value: atlas.countries.length, label: t('atlas.countries') },
    { value: atlas.tripsSealed,      label: t('atlas.sealedBooks') },
    { value: atlas.daysTravelled,    label: t('atlas.days') },
    { value: atlas.memories,         label: t('atlas.memories') },
    { value: atlas.reflections,      label: t('atlas.reflections') },
    { value: atlas.gemsRevealed,     label: t('atlas.gemsRevealed') },
    { value: atlas.gemsWhispered,    label: t('atlas.gemsWhispered') },
    { value: atlas.offRouteMemories, label: t('atlas.offRoute') },
    { value: atlas.threadsOpen,      label: t('atlas.threadsOpen') },
    { value: atlas.threadsResolved,  label: t('atlas.threadsResolved') },
  ] : [];

  const rec = atlas?.records || {};
  const records = [
    rec.longestTripDays && { label: t('atlas.longestTrip'), value: `${rec.longestTripDays.value} ${t('atlas.daysUnit')}`, sub: rec.longestTripDays.label },
    rec.mostMemoriesInTrip && rec.mostMemoriesInTrip.value > 0 && { label: t('atlas.mostMemories'), value: `${rec.mostMemoriesInTrip.value} ${t('atlas.memoriesUnit')}`, sub: rec.mostMemoriesInTrip.label },
    rec.mostCitiesInCountry && { label: t('atlas.mostCitiesInCountry'), value: `${rec.mostCitiesInCountry.value} ${t('atlas.citiesUnit')}`, sub: rec.mostCitiesInCountry.country },
    rec.firstSealedAt && { label: t('atlas.firstSeal'), value: fmtDate(rec.firstSealedAt) },
    rec.lastSealedAt && { label: t('atlas.lastSeal'), value: fmtDate(rec.lastSealedAt) },
  ].filter(Boolean);

  return (
    <div style={S.page}>
      <HomeButton onNavigate={onNavigate} />

      <div style={S.header}>
        <h1 style={S.title}>{t('atlas.title')}</h1>
        <p style={S.subtitle}>{t('atlas.subtitle')}</p>
      </div>

      {loading ? (
        <div style={S.muted}>…</div>
      ) : isEmpty ? (
        <div style={S.empty}>{t('atlas.empty')}</div>
      ) : (
        <>
          {/* Sprint 4B — the atlas as a flip-through booklet (Europe → country → city) */}
          <div style={{ marginBottom: 16 }}>
            <AtlasBook />
          </div>

          <div style={S.figuresGrid}>
            {figures.map(f => (
              <div key={f.label} style={S.figure}>
                <div style={S.figureValue}>{f.value}</div>
                <div style={S.figureLabel}>{f.label}</div>
              </div>
            ))}
          </div>

          {records.length > 0 && (
            <div style={S.recordsCard}>
              <div style={S.recordsHeading}>{t('atlas.records')}</div>
              {records.map(r => (
                <div key={r.label} style={S.recordRow}>
                  <div style={S.recordLabel}>{r.label}</div>
                  <div style={S.recordRight}>
                    <span style={S.recordValue}>{r.value}</span>
                    {r.sub && <span style={S.recordSub}>{r.sub}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Sprint 4A — city postcards earned by lighting whole constellations.
          Shown independently of sealed-trip figures (a postcard can be earned
          before a book is sealed). */}
      {!loading && <PostcardCollection />}
    </div>
  );
}

const S = {
  page: { minHeight: '100vh', background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)', padding: '20px', paddingBottom: '40px', position: 'relative' },
  header: { textAlign: 'center', margin: '60px 0 24px' },
  title: { fontSize: 40, fontWeight: 900, color: theme.brownDark, margin: '0 0 6px', letterSpacing: '1px', fontFamily: "'Noto Serif', serif" },
  subtitle: { fontSize: 14, color: theme.brown, margin: 0, fontStyle: 'italic' },
  muted: { textAlign: 'center', color: theme.sand, fontSize: 14, padding: '40px 0' },
  empty: { textAlign: 'center', color: theme.brown, fontSize: 15, fontStyle: 'italic', padding: '48px 24px', background: theme.card, borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
  figuresGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 },
  figure: { background: theme.card, borderRadius: 14, padding: '16px 10px', textAlign: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' },
  figureValue: { fontSize: 26, fontWeight: 800, color: theme.blueDeep, lineHeight: 1 },
  figureLabel: { fontSize: 10, color: theme.brown, textTransform: 'uppercase', letterSpacing: '0.4px', marginTop: 5 },
  recordsCard: { background: theme.card, borderRadius: 16, padding: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
  recordsHeading: { fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.terracottaDark, marginBottom: 12 },
  recordRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: '10px 0', borderBottom: `1px solid ${theme.creamDeep}` },
  recordLabel: { fontSize: 14, color: theme.brownDark, fontWeight: 600 },
  recordRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end' },
  recordValue: { fontSize: 14, fontWeight: 700, color: theme.terracotta },
  recordSub: { fontSize: 12, color: theme.brown, marginTop: 2 },
};
